import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";

export interface AutocompleteProps
  extends React.ComponentPropsWithoutRef<"div"> {
  options: { label: string; value: string | number }[];
  defaultValue?: string;
  handleSelect: (value: string | number) => void;
  handleTextInputChange?: (value: string) => void;
  loading?: boolean;
  renderInput?: (params: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }) => React.ReactNode;
}

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const DefaultInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const Dropdown = styled.ul<{ show: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  list-style: none;
  padding: 0;
  margin: 4px 0 0 0;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  display: ${(props) => (props.show ? "block" : "none")};
  z-index: 1000;
`;

const Option = styled.li`
  padding: 8px 12px;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const LoadingOption = styled.li`
  padding: 8px 12px;
  color: #666;
  font-style: italic;
`;

const Autocomplete = ({
  options,
  defaultValue = "",
  handleSelect,
  handleTextInputChange,
  loading = false,
  renderInput,
  ...props
}: AutocompleteProps) => {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasUserTyped = useRef(false);

  // Adopt `defaultValue` when it arrives asynchronously (e.g. a form populated
  // from a fetch), but never fight the user once they've started typing.
  useEffect(() => {
    if (!hasUserTyped.current) {
      setInputValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [options, inputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    hasUserTyped.current = true;
    setInputValue(value);
    setIsOpen(true);
    handleTextInputChange?.(value);

    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredOptions(filtered);
  };

  const handleOptionClick = (option: {
    label: string;
    value: string | number;
  }) => {
    setInputValue(option.label);
    handleSelect(option.value);
    setIsOpen(false);
  };

  const inputProps = {
    value: inputValue,
    onChange: handleInputChange,
    onFocus: () => setIsOpen(true),
  };

  return (
    <Container ref={containerRef} {...props}>
      {renderInput ? (
        <div onFocus={() => setIsOpen(true)}>{renderInput(inputProps)}</div>
      ) : (
        <DefaultInput type="text" {...inputProps} />
      )}
      <Dropdown show={isOpen}>
        {loading ? (
          <LoadingOption>Loading shift leads...</LoadingOption>
        ) : filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => (
            <Option key={index} onClick={() => handleOptionClick(option)}>
              {option.label}
            </Option>
          ))
        ) : (
          <LoadingOption>No results found</LoadingOption>
        )}
      </Dropdown>
    </Container>
  );
};

export default Autocomplete;
